/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HeaderModule
 */

import { Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { StateService } from '@uirouter/core';
import { takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';
import { L10nService } from '@vmw/ngx-vip';
import { StringService } from 'string-service';
import { IAviDropdownOption } from 'standalone-components/avi-dropdown/avi-dropdown.types';
import { createDropdownOption } from 'ng/shared/utils/dropdown.utils';
import { ActiveUserProfileService } from 'ajs/modules/core/services/active-user-profile/active-user-profile.service';

import {
    selectIsSuperuser,
    selectTenants,
} from 'ng/root-store/user-profile/user-profile.selectors';

import { naturalSort } from 'ng/shared/utils/natural-sort.utils';
import { StateManager } from 'ajs/modules/core/services/state-manager/state-manager.service';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import * as l10n from './avi-main-header-tenant-selector.l10n';
import './avi-main-header-tenant-selector.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component showing the tenant selection dropdown.
 * @author alextsg
 */
@Component({
    selector: 'avi-main-header-tenant-selector',
    templateUrl: './avi-main-header-tenant-selector.component.html',
})
export class AviMainHeaderTenantSelectorComponent implements OnDestroy {
    /**
     * Dropdown options of different tenants on the controller.
     */
    public tenantOptions: IAviDropdownOption[] = [];

    public selectedTenantRef: string;

    public selectedTenantName: string;

    public readonly l10nKeys = l10nKeys;

    private readonly ngDestroyed$ = new Subject();

    constructor(
        store: Store,
        private readonly auth: Auth,
        l10nService: L10nService,
        private readonly $state: StateService,
        private readonly stateManagerService: StateManager,
        private readonly stringService: StringService,
        private readonly activeUserProfileService: ActiveUserProfileService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.selectedTenantRef = auth.getCurrentTenantRef();

        combineLatest([store.select(selectTenants), store.select(selectIsSuperuser)])
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe(([tenants, isSuperuser]) => {
                const tenantOptions = tenants
                    .map(({ url, name }) => createDropdownOption(url, name))
                    .sort(({ label: labelA }, { label: labelB }) => naturalSort(labelA, labelB));

                if (tenantOptions.length && isSuperuser) {
                    const allTenantsOption = createDropdownOption(
                        '*',
                        l10nService.getMessage(l10nKeys.allTenantsLabel),
                    );

                    tenantOptions.unshift(allTenantsOption);
                }

                this.tenantOptions = tenantOptions;
                this.setSelectedTenantName();
            });
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Handle tenant selection change. Redirect to the closest available state.
     */
    public handleTenantChange(): void {
        this.setSelectedTenantName();

        const { selectedTenantRef } = this;
        const tenantName = this.stringService.name(selectedTenantRef) || selectedTenantRef;
        const oldTenantName = this.auth.getTenantName();
        const {
            current: currentState,
            params: currentStateParams,
        } = this.$state;

        if (tenantName === oldTenantName) {
            return;
        }

        const updatedStateParams = {
            ...currentStateParams,
            tenantName,
        };

        this.auth.setTenant(tenantName)
            .then(() => {
                const isAllowedState = this.stateManagerService.isAllowedState(
                    currentState,
                    updatedStateParams,
                    oldTenantName,
                );

                if (isAllowedState) {
                    return this.$state.go(currentState, updatedStateParams);
                } else {
                    const target = this.stateManagerService.getClosestAllowedState(
                        currentState,
                        updatedStateParams,
                        oldTenantName,
                    );

                    return this.$state.go(target, { tenantName });
                }
            })
            .catch(() => {
                this.auth.logout(true, false);
            });
    }

    /**
     * Load user profile on tenant dropdown open.
     */
    public handleTenantDropdownOpenAndClose(dropdownOpenStatus: boolean): void {
        if (dropdownOpenStatus) {
            this.activeUserProfileService.loadUserProfile();
        }
    }

    /**
     * Set the selected tenant name based on the selected tenant ref.
     */
    private setSelectedTenantName(): void {
        this.selectedTenantName = this.tenantOptions.find(
            ({ value }) => value === this.selectedTenantRef,
        ).label;
    }
}
